import React, { useCallback, useEffect, useState } from 'react';
import { Link, useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { SuperAdminAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';
import './database-viewer.css';


const NULL_SENTINEL = '\\N';


function pathFor(alias, schema, table, extra = '') {
  let url = '/super-admin/db-viewer';
  if (alias && schema) {
    url += `/${encodeURIComponent(alias)}/${encodeURIComponent(schema)}`;
  }
  if (table) url += `/${encodeURIComponent(table)}`;
  if (extra) url += extra;
  return url;
}


function Warning() {
  return (
    <div className="dbv-warn">
      <strong>Heads up —</strong> this is a low-level, direct database editor
      (pgAdmin-style). Edits bypass application-level validation and Django
      signals. Super admins only — organisation admins cannot open this page.
      <span className="dbv-badge">Edit enabled</span>
      <span className="dbv-badge">Insert enabled</span>
      <span className="dbv-badge">Delete enabled</span>
    </div>
  );
}


function HomeView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [draft, setDraft] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(q); }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    SuperAdminAPI.dbViewer.home({ q })
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q]);

  const submit = (event) => {
    event.preventDefault();
    const next = draft.trim();
    setSearchParams(next ? { q: next } : {});
  };

  if (loading && !data) return <Loader label="Loading databases…" />;

  return (
    <>
      <PageHeader
        title="🗄️ Database Viewer"
        subtitle="Browse and edit tables across every connected database and tenant schema."
      />
      <ErrorBox error={error} />
      <Warning />
      <section className="ty-card">
        <form className="dbv-search" onSubmit={submit}>
          <input
            className="ty-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Filter schemas or table names…"
          />
          <button type="submit" className="ty-btn">Filter</button>
          {q && (
            <button type="button" className="ty-btn ty-btn--ghost" onClick={() => setSearchParams({})}>
              Clear
            </button>
          )}
        </form>
      </section>
      {(data?.databases || []).map((db) => (
        <section className="ty-card" key={db.alias} style={{ marginTop: 14 }}>
          <div className="ty-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <strong>{db.alias}</strong>
              <span className="dbv-badge">{(db.schemas || []).length} schemas</span>
            </div>
            <div className="dbv-meta">
              <strong>{db.name}</strong>
              {db.host ? ` @ ${db.host}` : ''}
              <div>{db.engine}</div>
            </div>
          </div>
          {db.error && <p className="ty-error" style={{ marginTop: 10 }}>{db.error}</p>}
          {!db.error && !(db.schemas || []).length && (
            <p className="dbv-null" style={{ marginTop: 10 }}>
              {q ? `No schemas or tables match “${q}”.` : 'No schemas found.'}
            </p>
          )}
          <div className="dbv-schema-list">
            {(db.schemas || []).map((sch) => (
              <div key={sch.name} className="dbv-schema-card">
                <Link to={pathFor(db.alias, sch.name)}>
                  <h3>{sch.name}</h3>
                </Link>
                {sch.label !== sch.name && <div className="dbv-meta">{sch.label}</div>}
                <div className="dbv-meta">{sch.table_count} tables</div>
                {(sch.tables || []).length > 0 && (
                  <ul className="dbv-chips">
                    {sch.tables.map((table) => (
                      <li key={table}>
                        <Link to={pathFor(db.alias, sch.name, table)}>{table}</Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}


function SchemaView({ alias, schema }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [draft, setDraft] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(q); }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    SuperAdminAPI.dbViewer.schema(alias, schema, { q })
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [alias, schema, q]);

  if (loading && !data) return <Loader label="Loading tables…" />;

  return (
    <>
      <PageHeader
        title={`🗄️ ${schema}`}
        subtitle={data ? `${data.db_name} (${alias}) · ${data.table_count} tables` : ''}
        actions={
          <Link className="ty-btn ty-btn--ghost" to="/super-admin/db-viewer">← All schemas</Link>
        }
      />
      <ErrorBox error={error} />
      <section className="ty-card">
        <form
          className="dbv-search"
          onSubmit={(event) => {
            event.preventDefault();
            const next = draft.trim();
            setSearchParams(next ? { q: next } : {});
          }}
        >
          <input
            className="ty-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Filter tables by name…"
          />
          <button type="submit" className="ty-btn">Filter</button>
        </form>
        {(data?.tables || []).length ? (
          <ul className="dbv-chips">
            {data.tables.map((table) => (
              <li key={table}>
                <Link to={pathFor(alias, schema, table)}>{table}</Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dbv-null" style={{ marginTop: 12 }}>
            {q ? `No tables match “${q}”.` : 'No tables found.'}
          </p>
        )}
      </section>
    </>
  );
}


function cellText(cell) {
  if (!cell || cell.is_null) return null;
  if (cell.value === null || cell.value === undefined) return '';
  if (typeof cell.value === 'object') return JSON.stringify(cell.value);
  return String(cell.value);
}


function TableView({ alias, schema, table }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftQ, setDraftQ] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState([]);
  const [conjunction, setConjunction] = useState('AND');
  const [editing, setEditing] = useState(null);

  const queryKey = searchParams.toString();

  useEffect(() => {
    setDraftQ(searchParams.get('q') || '');
    const cols = searchParams.getAll('f_col');
    const ops = searchParams.getAll('f_op');
    const vals = searchParams.getAll('f_val');
    const next = cols.map((column, idx) => ({
      column,
      op: ops[idx] || 'eq',
      value: vals[idx] || '',
    }));
    setFilters(next);
    setConjunction((searchParams.get('f_conj') || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND');
  }, [queryKey]);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = {};
    ['q', 'order', 'dir', 'page', 'f_conj'].forEach((key) => {
      const val = searchParams.get(key);
      if (val) params[key] = val;
    });
    SuperAdminAPI.dbViewer.table(alias, schema, table, {
      ...params,
      f_col: searchParams.getAll('f_col'),
      f_op: searchParams.getAll('f_op'),
      f_val: searchParams.getAll('f_val'),
    })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [alias, schema, table, queryKey, searchParams]);

  useEffect(() => { reload(); }, [reload]);

  const applyFilters = (event) => {
    event.preventDefault();
    const next = new URLSearchParams();
    const q = draftQ.trim();
    if (q) next.set('q', q);
    if (searchParams.get('order')) next.set('order', searchParams.get('order'));
    if (searchParams.get('dir')) next.set('dir', searchParams.get('dir'));
    filters.forEach((item) => {
      next.append('f_col', item.column);
      next.append('f_op', item.op);
      next.append('f_val', item.value);
    });
    if (filters.length) next.set('f_conj', conjunction);
    setSearchParams(next);
  };

  const setSort = (column) => {
    const next = new URLSearchParams(searchParams);
    const current = searchParams.get('order');
    const dir = searchParams.get('dir') || 'asc';
    next.set('order', column);
    next.set('dir', current === column && dir === 'asc' ? 'desc' : 'asc');
    next.delete('page');
    setSearchParams(next);
  };

  const goPage = (page) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    setSearchParams(next);
  };

  const operators = data?.filter_operators || {};
  const operatorEntries = Object.entries(operators);

  const saveCell = async (pkValue, column, newVal) => {
    const setNull = newVal === NULL_SENTINEL;
    try {
      const result = await SuperAdminAPI.dbViewer.updateCell(alias, schema, table, {
        column,
        pk_value: pkValue,
        value: setNull ? '' : newVal,
        set_null: setNull ? '1' : '0',
      });
      if (!result.ok) throw new Error(result.error || 'Update failed');
      notify.success('Cell saved.');
      setEditing(null);
      reload();
    } catch (err) {
      notify.error(err?.response?.data?.error || err.message || 'Update failed');
    }
  };

  const deleteRow = async (pkValue) => {
    if (!window.confirm(`Permanently DELETE this row from ${table}? This cannot be undone.`)) {
      return;
    }
    try {
      await SuperAdminAPI.dbViewer.deleteRow(alias, schema, table, { pk_value: pkValue });
      notify.success('Row deleted.');
      reload();
    } catch (err) {
      notify.error(err?.response?.data?.error || err.message || 'Delete failed');
    }
  };

  if (loading && !data) return <Loader label="Loading table…" />;

  return (
    <>
      <PageHeader
        title={`🗄️ ${table}`}
        subtitle={data ? `${data.db_name} (${alias}.${schema})` : ''}
        actions={
          <>
            {data?.can_add && data?.is_mutable && (
              <Link className="ty-btn" to={pathFor(alias, schema, table, '/add')}>Insert Row</Link>
            )}
            <Link className="ty-btn ty-btn--ghost" to={pathFor(alias, schema)}>← All tables</Link>
          </>
        }
      />
      <ErrorBox error={error} />
      <section className="ty-card">
        <div className="dbv-meta">
          {data?.total_rows ?? 0} rows
          {data?.pk_column
            ? <span className="dbv-badge dbv-badge-pk">PK: {data.pk_column}</span>
            : <span className="dbv-badge">No single-column PK — read-only</span>}
        </div>
        {data?.can_edit && data?.is_mutable && (
          <p className="dbv-meta" style={{ marginTop: 8 }}>
            Double-click any cell to edit. Enter saves, Esc cancels. Type <code>\N</code> for NULL.
          </p>
        )}
      </section>

      <section className="ty-card" style={{ marginTop: 14 }}>
        <form onSubmit={applyFilters}>
          <div className="dbv-search" style={{ marginBottom: 12 }}>
            <input
              className="ty-input"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder="Search across all columns…"
            />
            <button type="submit" className="ty-btn">Search</button>
          </div>
          <div className="dbv-meta" style={{ marginBottom: 8 }}>
            Match
            <select
              className="ty-select"
              style={{ width: 'auto', minHeight: 32, margin: '0 6px', display: 'inline-block' }}
              value={conjunction}
              onChange={(e) => setConjunction(e.target.value)}
            >
              <option value="AND">ALL (AND)</option>
              <option value="OR">ANY (OR)</option>
            </select>
            of the following conditions:
          </div>
          {filters.map((item, idx) => (
            <div className="dbv-filter-row" key={idx}>
              <select
                className="ty-select"
                value={item.column}
                onChange={(e) => {
                  const next = filters.slice();
                  next[idx] = { ...next[idx], column: e.target.value };
                  setFilters(next);
                }}
              >
                {(data?.columns || []).map((col) => (
                  <option key={col.name} value={col.name}>{col.name}</option>
                ))}
              </select>
              <select
                className="ty-select"
                value={item.op}
                onChange={(e) => {
                  const next = filters.slice();
                  next[idx] = { ...next[idx], op: e.target.value };
                  setFilters(next);
                }}
              >
                {operatorEntries.map(([key, spec]) => (
                  <option key={key} value={key}>{spec.label}</option>
                ))}
              </select>
              <input
                className="ty-input"
                value={item.value}
                readOnly={operators[item.op] && operators[item.op].needs_value === false}
                onChange={(e) => {
                  const next = filters.slice();
                  next[idx] = { ...next[idx], value: e.target.value };
                  setFilters(next);
                }}
                placeholder="value"
              />
              <button
                type="button"
                className="ty-btn ty-btn--ghost ty-btn--sm"
                onClick={() => setFilters(filters.filter((_, i) => i !== idx))}
              >
                Remove
              </button>
            </div>
          ))}
          <div className="ty-row" style={{ gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="ty-btn ty-btn--ghost ty-btn--sm"
              onClick={() => setFilters([
                ...filters,
                { column: data?.columns?.[0]?.name || '', op: 'eq', value: '' },
              ])}
            >
              Add condition
            </button>
            <button type="submit" className="ty-btn ty-btn--sm">Apply Filters</button>
            {(filters.length || draftQ) ? (
              <button
                type="button"
                className="ty-btn ty-btn--ghost ty-btn--sm"
                onClick={() => { setDraftQ(''); setFilters([]); navigate(pathFor(alias, schema, table)); }}
              >
                Clear all
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="ty-card" style={{ marginTop: 14 }}>
        {data?.fetch_error && <div className="ty-error">{data.fetch_error}</div>}
        <div className="dbv-table-wrap">
          <table className="dbv-grid">
            <thead>
              <tr>
                {data?.can_delete && data?.is_mutable && <th />}
                {(data?.columns || []).map((col) => (
                  <th key={col.name}>
                    <button type="button" onClick={() => setSort(col.name)}>
                      {col.name}
                      {data.order === col.name ? (data.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                    </button>
                    {col.name === data.pk_column && <span className="dbv-badge dbv-badge-pk">PK</span>}
                    <span className="dbv-type">{col.type}{!col.null_ok ? ' · NOT NULL' : ''}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((row) => (
                <tr key={String(row.pk_value)}>
                  {data?.can_delete && data?.is_mutable && (
                    <td>
                      <button
                        type="button"
                        className="ty-btn ty-btn--ghost ty-btn--sm"
                        onClick={() => deleteRow(row.pk_value)}
                        title="Delete row"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                  {row.cells.map((cell) => {
                    const isEditing = editing
                      && String(editing.pk) === String(row.pk_value)
                      && editing.column === cell.column;
                    return (
                      <td
                        key={cell.column}
                        className={`dbv-cell${data?.can_edit && data?.is_mutable ? ' editable' : ''}`}
                        onDoubleClick={() => {
                          if (!(data?.can_edit && data?.is_mutable)) return;
                          setEditing({
                            pk: row.pk_value,
                            column: cell.column,
                            value: cell.is_null ? '' : cellText(cell),
                          });
                        }}
                      >
                        {isEditing ? (
                          <input
                            className="dbv-cell-editor"
                            autoFocus
                            defaultValue={editing.value}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveCell(row.pk_value, cell.column, e.target.value);
                              } else if (e.key === 'Escape') {
                                setEditing(null);
                              }
                            }}
                            onBlur={(e) => saveCell(row.pk_value, cell.column, e.target.value)}
                          />
                        ) : cell.is_null ? (
                          <span className="dbv-null">NULL</span>
                        ) : (
                          cellText(cell)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!(data?.rows || []).length && (
                <tr>
                  <td colSpan={(data?.columns || []).length + 1} className="dbv-null" style={{ padding: 24, textAlign: 'center' }}>
                    No rows found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {data && data.num_pages > 1 && (
          <div className="dbv-pager">
            <button type="button" disabled={!data.has_previous} onClick={() => goPage(1)}>« First</button>
            <button type="button" disabled={!data.has_previous} onClick={() => goPage(data.previous_page)}>‹ Prev</button>
            {(data.page_window || []).map((page) => (
              <button
                key={page}
                type="button"
                className={page === data.page_number ? 'active' : ''}
                onClick={() => goPage(page)}
              >
                {page}
              </button>
            ))}
            <button type="button" disabled={!data.has_next} onClick={() => goPage(data.next_page)}>Next ›</button>
            <button type="button" disabled={!data.has_next} onClick={() => goPage(data.num_pages)}>Last »</button>
            <span className="dbv-meta" style={{ border: 'none' }}>
              Page {data.page_number} of {data.num_pages}
            </span>
          </div>
        )}
      </section>
    </>
  );
}


function AddRowView({ alias, schema, table }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    SuperAdminAPI.dbViewer.addRowForm(alias, schema, table)
      .then((payload) => {
        setData(payload);
        setFields((payload.columns || []).map((col) => ({
          column: col.name,
          include: col.name !== payload.pk_column,
          set_null: false,
          value: '',
          null_ok: col.null_ok,
        })));
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [alias, schema, table]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await SuperAdminAPI.dbViewer.addRow(alias, schema, table, { fields });
      notify.success(`Row inserted into '${table}'.`);
      navigate(pathFor(alias, schema, table));
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader label="Loading columns…" />;

  return (
    <>
      <PageHeader
        title={`Insert row — ${table}`}
        subtitle="Untick Include to let the database fill defaults / serial keys. Tick NULL to store SQL NULL."
        actions={
          <Link className="ty-btn ty-btn--ghost" to={pathFor(alias, schema, table)}>← Back to table</Link>
        }
      />
      <ErrorBox error={error} />
      <section className="ty-card">
        <form onSubmit={save}>
          <div className="dbv-table-wrap">
            <table className="dbv-grid">
              <thead>
                <tr>
                  <th>Column</th>
                  <th>Include</th>
                  <th>NULL</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, idx) => (
                  <tr key={field.column}>
                    <td>
                      <strong>{field.column}</strong>
                      {data?.pk_column === field.column && <span className="dbv-badge dbv-badge-pk">PK</span>}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={field.include}
                        onChange={(e) => {
                          const next = fields.slice();
                          next[idx] = { ...next[idx], include: e.target.checked };
                          setFields(next);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={field.set_null}
                        disabled={!field.include || !field.null_ok}
                        onChange={(e) => {
                          const next = fields.slice();
                          next[idx] = { ...next[idx], set_null: e.target.checked };
                          setFields(next);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className="ty-input"
                        value={field.value}
                        disabled={!field.include || field.set_null}
                        onChange={(e) => {
                          const next = fields.slice();
                          next[idx] = { ...next[idx], value: e.target.value };
                          setFields(next);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="ty-row" style={{ gap: 8, marginTop: 14 }}>
            <button type="submit" className="ty-btn" disabled={saving}>
              {saving ? 'Inserting…' : 'Insert Row'}
            </button>
            <Link className="ty-btn ty-btn--ghost" to={pathFor(alias, schema, table)}>Cancel</Link>
          </div>
        </form>
      </section>
    </>
  );
}


/**
 * Super Admin → Database Viewer.
 *
 * Superuser-only pgAdmin-style browser/editor. Organisation admins are
 * blocked by ``requireSuperAdmin`` in NAV and by 403 from the API.
 */
export default function DatabaseViewer() {
  const { alias, schema, table } = useParams();
  const addMatch = useMatch('/super-admin/db-viewer/:alias/:schema/:table/add');

  if (alias && schema && table && addMatch) {
    return <AddRowView alias={alias} schema={schema} table={table} />;
  }
  if (alias && schema && table) {
    return <TableView alias={alias} schema={schema} table={table} />;
  }
  if (alias && schema) {
    return <SchemaView alias={alias} schema={schema} />;
  }
  return <HomeView />;
}

export { pathFor };
